import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import {
  AppSettingsType,
  AppVariantType,
  APP_VARIANTS_ENUM,
} from "../../models/appSettings";
import { FilterDataResponse, ProductListResponse } from "../../models/API";
import { postData } from "../../helpers/dataSending";
import { createFiltersFromSQ } from "../../helpers/dynamicFilterSQ";
import { CompareProducts } from "../Compare/index";
import { Filters } from "../filters";
import { ResultProducts } from "../resultProducts";
import { Loading } from "../../components/Loading";
import { MainFilters } from "../MainFilters/index";
import { FiltersDataType, FiltersStateType } from "../../models/filters";
import { Error } from "../../components/Error";
// import { Spinner } from "../../components/Spinner"; //TODO: it needs styles
import { classNamesBasedOnAppVariant } from "../../helpers/shared";
import { CompareDataType } from "../../models/comapreResults";

import "./App.css";

// Default variant of app
export const StaticSettingsAppContext = React.createContext({
  APP_VARIANT: APP_VARIANTS_ENUM.CORE_SELECTION as AppVariantType,
  CLASSES_NAMES: classNamesBasedOnAppVariant(APP_VARIANTS_ENUM.CORE_SELECTION),
  APP_REF: null as MutableRefObject<any>,
});

export const App: React.FC<AppSettingsType> = (settings) => {
  const CLASSES_NAMES = classNamesBasedOnAppVariant(settings.appVariant);
  const STATIC_SETTINGS_APP_CONTEXT = {
    CLASSES_NAMES,
    APP_VARIANT: settings.appVariant,
    APP_REF: useRef(settings.applicationRoot),
  };

  // Elements used as dictionary
  const [dictionaryElements, setDictionaryElements] = useState(null);

  // State flags, loading is for startup only, working can be used as loader
  const [appState, setAppState] = useState({
    loading: true,
    error: false,
    working: false,
  });

  // Element assigned to decide what do we show - Compare or Core Selection and data for compare view
  const [compareResults, setCompareResults] = useState<CompareDataType>({
    view: false,
    results: [],
  });

  // Elements used for rendering filters
  const [filtersData, setFiltersData] = useState<FiltersDataType>({
    main: [],
    dynamic: [],
  });

  // Elements used for rendering ProductResults
  const [productResults, setProductResults] = useState(null);

  // Elements used to call for results
  const [filtersForResults, setFiltersForResults] =
    useState<FiltersStateType>(null);

  // Toggle filters visibility on mobile, null at startup is required (update useEffect in case changes)
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);

  const toggleMobileFilters = (e: React.PointerEvent<HTMLButtonElement>) => {
    setShowFilters((state) => !state);
  };

  useEffect(() => {
    // This should fire only once at the beggining
    if (settings.filterDataUrl && settings.productResultsUrl) {
      postData(
        settings.filterDataUrl,
        null,
        "GET",
        "filterData-" + settings.appVariant
      )
        .then((response: FilterDataResponse) => {
          setDictionaryElements(response.Translations);

          const availableFilters: FiltersDataType = {
            dynamic: response.LeftFilters.LeftFilterItems,
            main: response.TopFilters.TopFilterItems,
          };

          // Create filter set from url
          const newFilteringObject = createFiltersFromSQ(availableFilters);

          // Set all filters data
          setFiltersData(availableFilters);

          // Set filters from url
          response.LeftFilters.LeftFilterItems.forEach((singleFilter) => {
            const foundIndex = newFilteringObject.Dynamic.findIndex(
              (el) => el.Key === singleFilter.Key
            );
            if (singleFilter.Type === "checkboxes") {
              if (foundIndex === -1) {
                newFilteringObject.Dynamic.push({
                  Key: singleFilter.Key,
                  Checked: [],
                });
              }
            } else {
              if (foundIndex === -1) {
                newFilteringObject.Dynamic.push({
                  Key: singleFilter.Key,
                  FromValue: singleFilter.Values[0].Min,
                  ToValue: singleFilter.Values[0].Max,
                });
              } else {
                const itemsToMoveFromChecked =
                  newFilteringObject.Dynamic[foundIndex].Checked;
                newFilteringObject.Dynamic[foundIndex] = {
                  Key: singleFilter.Key,
                  FromValue: parseInt(itemsToMoveFromChecked[0]),
                  ToValue: parseInt(itemsToMoveFromChecked[1]),
                };
              }
            }
          });

          // Set filter state
          setFiltersForResults(newFilteringObject);
        })
        .catch((err) => {
          setAppState({ loading: false, error: true, working: false });
        });
    } else {
      setAppState({ loading: false, error: true, working: false });
      console.error("No API urls. Check attributes in app root element.");
    }
  }, [settings.filterDataUrl, settings.productResultsUrl, settings.appVariant]);

  useEffect(() => {
    if (filtersForResults) {
      // setAppState({ loading: false, error: false, working: true });
      const dataToSend = Object.assign({}, filtersForResults);
      postData(
        settings.productResultsUrl,
        dataToSend,
        "POST",
        "productListData-" + settings.appVariant
      )
        .then((response: ProductListResponse) => {
          setProductResults(response);
          setAppState({ loading: false, error: false, working: false });
        })
        .catch((err) => {
          setAppState({ loading: false, error: true, working: false });
        });
    }
  }, [filtersForResults, settings.productResultsUrl, settings.appVariant]);

  if (appState.loading) {
    return <Loading />;
  }

  // Error message
  if (appState.error && settings.errMessage) {
    return <Error message={settings.errMessage} />;
  }

  return compareResults.view && compareResults.results.length > 1 ? (
    <StaticSettingsAppContext.Provider value={STATIC_SETTINGS_APP_CONTEXT}>
      <CompareProducts
        compareResults={compareResults}
        setCompareResults={setCompareResults}
        inquiryUrl={settings.inquiryApiUrl}
        dictionary={{
          InquiryText: dictionaryElements.InquiryText,
          MakeInquiryText: dictionaryElements.MakeInquiryText,
          BackToResultsText: dictionaryElements.BackToResultsText,
          PropertyColumnText: dictionaryElements.PropertyColumnText,
          UnitColumnText: dictionaryElements.UnitColumnText,
        }}
      />
    </StaticSettingsAppContext.Provider>
  ) : (
    <StaticSettingsAppContext.Provider value={STATIC_SETTINGS_APP_CONTEXT}>
      {/*appState.working && <Spinner />*/}
      {filtersData.main.length > 0 && (
        <MainFilters
          type="desktop"
          filters={filtersData.main}
          filtersState={filtersForResults}
          updateFilters={setFiltersForResults}
        />
      )}
      <div className={CLASSES_NAMES.CLASS_HOLDER}>
        <div
          ref={filtersRef}
          className={`column filters${showFilters ? " active" : ""}`}
        >
          {showFilters && (
            <span
              className={
                "ig-filters-overlay-" + STATIC_SETTINGS_APP_CONTEXT.APP_VARIANT
              }
              onClick={toggleMobileFilters}
              style={{ left: filtersRef.current.offsetWidth }}
            ></span>
          )}
          {filtersData.main.length > 0 && (
            <MainFilters
              type="mobile"
              filters={filtersData.main}
              filtersState={filtersForResults}
              updateFilters={setFiltersForResults}
            />
          )}
          {filtersData.dynamic.length > 0 && (
            <Filters
              filterData={filtersData.dynamic}
              filterHeader={dictionaryElements.LeftFiltersTopText}
              filterState={filtersForResults}
              setFilterState={setFiltersForResults}
            />
          )}
          <div className="button-mobile">
            <button className="cancel" onClick={toggleMobileFilters}>
              {dictionaryElements.CancelText}
            </button>
          </div>
        </div>
        <div className="column">
          {productResults && dictionaryElements && (
            <ResultProducts
              compareResults={compareResults}
              productResults={productResults}
              dictionaryElements={dictionaryElements}
              compareUrl={settings.compareApiUrl}
              setCompareResults={setCompareResults}
              inquiryUrl={settings.inquiryApiUrl}
              toggleMobileFilters={toggleMobileFilters}
            />
          )}
        </div>
      </div>
    </StaticSettingsAppContext.Provider>
  );
};
